import { TallyConnectionStatus } from '../../types/tally';
import axios from 'axios';
import posthog from 'posthog-js';
import { toast } from 'react-toastify';

export const checkTallyConnection =
  async (): Promise<TallyConnectionStatus> => {
    try {
      const response = await axios.get('http://localhost:9000/tally', {
        headers: {
          Accept: 'text/xml',
        },
        timeout: 5000,
      });

      if (response.data.includes('Running')) {
        return {
          isConnected: true,
          message: 'Successfully connected to Tally',
        };
      } else {
        return {
          isConnected: false,
          message: 'Received unexpected response from Tally',
        };
      }
    } catch (error) {
      console.error('Error checking Tally connection:', error);
      return {
        isConnected: false,
        message:
          error instanceof Error ? error.message : 'Failed to connect to Tally',
      };
    }
  };

// Parse XML response from Tally
const parseXmlResponse = (xmlString: string) => {
  try {
    // Create a parser and parse the XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    // Extract values
    const getElementValue = (tagName: string) => {
      const element = xmlDoc.querySelector(tagName);
      return element ? element.textContent || '0' : '0';
    };

    // Extract all LINEERROR entries if any (aggregate into a single string and keep array)
    const lineErrorElements = Array.from(xmlDoc.querySelectorAll('LINEERROR'));
    const lineErrorsArray = lineErrorElements
      .map(el => el.textContent?.trim() || '')
      .filter(s => s !== '');
    const lineError = lineErrorsArray.length ? lineErrorsArray.join('; ') : '';

    // Build result object
    const result = {
      CREATED: getElementValue('CREATED'),
      ALTERED: getElementValue('ALTERED'),
      DELETED: getElementValue('DELETED'),
      ERRORS: getElementValue('ERRORS'),
      CANCELLED: getElementValue('CANCELLED'),
      EXCEPTIONS: getElementValue('EXCEPTIONS'),
      LINEERROR: lineError,
      LINEERRORS: lineErrorsArray,
      COMBINED: getElementValue('COMBINED'),
      IGNORED: getElementValue('IGNORED'),
    };

    return result;
  } catch (error) {
    console.error('Error parsing XML response:', error);

    // Fallback using regex if DOM parsing fails
    const extractValue = (tag: string) => {
      const regex = new RegExp(`<${tag}>([^<]*)</${tag}>`, 'i');
      const match = xmlString.match(regex);
      return match ? match[1].trim() : '0';
    };

    const extractLineErrors = () => {
      const regex = /<LINEERROR>([^<]*)<\/LINEERROR>/gi;
      const matches = [...xmlString.matchAll(regex)];
      const arr = matches.map(m => (m[1] || '').trim()).filter(s => s !== '');
      return {
        joined: arr.length ? arr.join('; ') : '',
        arr,
      };
    };

    const lineErrors = extractLineErrors();
    return {
      CREATED: extractValue('CREATED'),
      ALTERED: extractValue('ALTERED'),
      DELETED: extractValue('DELETED'),
      ERRORS: extractValue('ERRORS'),
      CANCELLED: extractValue('CANCELLED'),
      EXCEPTIONS: extractValue('EXCEPTIONS'),

      LINEERROR: lineErrors.joined,
      LINEERRORS: lineErrors.arr,
      COMBINED: extractValue('COMBINED'),
      IGNORED: extractValue('IGNORED'),
    };
  }
};

// Import vouchers to Tally
export const importVouchersToTally = async (xmlData: string) => {
  try {
    const response = await fetch('http://localhost:9000/tally', {
      method: 'POST',
      body: xmlData,
    });

    const responseText = await response.text();
    const parsedResponse = parseXmlResponse(responseText);

    if (!response.ok) {
      // Parse error details from response if available
      if (parsedResponse.LINEERROR) {
        displayLineErrorToast(parsedResponse.LINEERROR);
      }
      if (parsedResponse.ERRORS) {
        toast.error(`Tally Errors: ${parsedResponse.ERRORS}`);
      }

      posthog.capture('tally_import_failure', {
        error_type: 'http_error',
        message: `HTTP error! Status: ${response.status}`,
        payload: xmlData,
        tally_response: responseText,
        status_code: response.status,
      });

      return {
        success: false,
        message: `HTTP error! Status: ${response.status}`,
        parsedResponse,
      };
    }

    // Parse all counts
    const createdCount = parseInt(parsedResponse.CREATED || '0', 10);
    const alteredCount = parseInt(parsedResponse.ALTERED || '0', 10);
    const deletedCount = parseInt(parsedResponse.DELETED || '0', 10);
    const errorCount = parseInt(parsedResponse.ERRORS || '0', 10);
    const exceptionCount = parseInt(parsedResponse.EXCEPTIONS || '0', 10);
    const hasLineErrors = parsedResponse.LINEERROR && parsedResponse.LINEERROR.trim() !== '';

    // Calculate total successful operations and total errors
    const totalSuccessful = createdCount + alteredCount + deletedCount;
    const totalErrors = errorCount + exceptionCount + (hasLineErrors ? 1 : 0);

    // Show success messages for each type of operation
    if (totalSuccessful > 0) {
      const successDetails = [];
      if (createdCount > 0) successDetails.push(`${createdCount} created`);
      if (alteredCount > 0) successDetails.push(`${alteredCount} modified`);
      if (deletedCount > 0) successDetails.push(`${deletedCount} deleted`);
      
      toast.success(`Tally operations completed: ${successDetails.join(', ')}`, {
        autoClose: 8000,
      });
    }

    // Show error messages
    if (hasLineErrors) {
      displayLineErrorToast(parsedResponse.LINEERROR);
    }
    
    if (errorCount > 0) {
      toast.error(`${errorCount} error(s) occurred`, { autoClose: 8000 });
    }
    
    if (exceptionCount > 0) {
      toast.warning(
        `${exceptionCount} exception(s) occurred due to unbalanced invoice output`,
        { autoClose: 8000 }
      );
    }

    if (totalErrors > 0) {
      posthog.capture('tally_import_failure', {
        error_type: 'tally_logic_error',
        message: `Tally reported ${totalErrors} errors`,
        payload: xmlData,
        tally_response: parsedResponse,
        error_counts: {
          errors: errorCount,
          exceptions: exceptionCount,
          line_errors: hasLineErrors ? 1 : 0,
        },
      });
    }

    return {
      success: totalSuccessful > 0 && totalErrors === 0,
      message: responseText,
      parsedResponse,
      stats: {
        created: createdCount,
        altered: alteredCount,
        deleted: deletedCount,
        errors: errorCount,
        exceptions: exceptionCount,
        hasLineErrors,
        totalSuccessful,
        totalErrors
      }
    };
  } catch (error) {
    console.error('Error sending data to Tally:', error);
    toast.error(
      error instanceof Error ? error.message : 'Failed to connect to Tally',
    );

    posthog.capture('tally_import_failure', {
      error_type: 'exception',
      message: error instanceof Error ? error.message : 'Unknown error during fetch',
      payload: xmlData,
    });

    return {
      success: false,
      message:
        error instanceof Error ? error.message : 'Failed to connect to Tally',
    };
  }
};

const MAX_TOAST_LENGTH = 200; // Maximum characters to show in toast

export const displayLineErrorToast = (lineError: string) => {
  if (!lineError || lineError.trim() === '') return;

  const truncatedError = lineError.length > MAX_TOAST_LENGTH
    ? `${lineError.substring(0, MAX_TOAST_LENGTH)}...`
    : lineError;

  // Copy full error to clipboard
  if (lineError.length > MAX_TOAST_LENGTH) {
    navigator.clipboard.writeText(lineError).then(() => {
      toast.info('Full error message copied to clipboard', { autoClose: 3000 });
    }).catch(console.error);
  }

  toast.error(
    `Tally LINEERROR: ${truncatedError}${lineError.length > MAX_TOAST_LENGTH ? ' (Full error copied to clipboard)' : ''}`,
    { autoClose: 8000 }
  );
};

export type { TallyConnectionStatus };
